/* Settings → People. Who can sign a station in to this salon.
 *
 * Only an owner sees this tab at all — see the `v-if` in settings.js — and only
 * an owner can do anything on it. That is enforced in the database twice over,
 * by the owner policies and the last-owner trigger in
 * server/schema/09-owner-members.sql; what this screen does is say so before the
 * button is pressed, so a refusal reads as a sentence next to the control
 * rather than as a Postgres error in a toast.
 *
 * The decisions are all in js/rules/members.js, tested. This file is markup.
 */
import * as Vue from 'vue';
import { members } from '../members.js';
import { refuseCreate, seatsLeft, wouldStrandTenant } from '../rules/members.js';
import { store } from '../store.js';

export const PeopleSettings = {
  name: 'PeopleSettings',
  setup: function () {
    var email = Vue.ref('');
    var role = Vue.ref('station');
    var confirmRemove = Vue.ref(null);

    var rows = Vue.computed(function () { return members.state.rows; });
    var you = Vue.computed(function () {
      return (store.auth.user && store.auth.user.id) || null;
    });

    var left = Vue.computed(function () {
      return seatsLeft(rows.value, members.state.seats);
    });

    /* The reason the Add button is off, or null. Recomputed as the box is typed
     * in, so "that address is already on this salon" appears while somebody is
     * still looking at the address rather than after they commit to it. */
    var refusal = Vue.computed(function () {
      if (!email.value.trim()) return null;
      return refuseCreate('owner', email.value, rows.value, members.state.seats);
    });

    // Whether removing or demoting this person would leave the salon with
    // nobody who can manage it. The trigger refuses it too; this is what stops
    // the button looking available in the first place.
    function strands(row, nextRole) {
      return wouldStrandTenant(rows.value, row.userId, nextRole);
    }

    async function submit() {
      if (refusal.value || !email.value.trim()) return;
      var ok = await members.create(email.value.trim(), role.value);
      if (ok) { email.value = ''; role.value = 'station'; }
    }

    async function changeRole(row, next) {
      if (strands(row, next)) return;
      await members.setRole(row.userId, next);
    }

    async function doRemove(row) {
      confirmRemove.value = null;
      await members.remove(row.userId);
    }

    function since(value) {
      if (!value) return 'never signed in';
      var d = new Date(value);
      return 'last in ' + d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }

    Vue.onMounted(function () { members.load(); });

    return {
      state: members.state,
      rows: rows, you: you, left: left, refusal: refusal,
      email: email, role: role,
      confirmRemove: confirmRemove,
      strands: strands, since: since,
      submit: submit, changeRole: changeRole, doRemove: doRemove,
      forget: members.forget
    };
  },
  template: [
    '<div class="config__intro">',
    '  <h2>People</h2>',
    '  <p><span>Who can sign a station in to this salon. An <strong>owner</strong> can manage',
    '    accounts and settings; the <strong>front desk</strong> signs in and works the board.</span></p>',
    '  <p class="config__where">Accounts are for this salon only. Somebody who already has an',
    '    account somewhere else cannot be added here &mdash; an account belongs to one salon.</p>',
    '</div>',
    '',
    /* Shown once, and there is no endpoint that would return it again. Loud on
       purpose: this is the only moment the password exists anywhere it can be
       read, and a salon that closes it without writing it down has to delete the
       account and make another. */
    '<div v-if="state.issued" class="people__issued">',
    '  <h3>Account made for {{ state.issued.email }}</h3>',
    '  <p>Write this password down now, or type it straight into the station. It is',
    '    <strong>not stored</strong> and cannot be shown again.</p>',
    '  <p class="people__password"><code>{{ state.issued.password }}</code></p>',
    '  <button type="button" class="btn btn--primary" @click="forget">I have it</button>',
    '</div>',
    '',
    '<form class="add-card" @submit.prevent="submit">',
    '  <h3>Add an account</h3>',
    '  <div class="add-card__row">',
    '    <input v-model="email" type="email" class="input" placeholder="Email address"',
    '      autocomplete="off" maxlength="120" />',
    '    <select v-model="role" class="input people__role-pick">',
    '      <option value="station">Front desk</option>',
    '      <option value="owner">Owner</option>',
    '    </select>',
    '    <button type="submit" class="btn btn--primary"',
    '      :disabled="state.busy || !!refusal || !email.trim()">',
    '      {{ state.busy ? "Making…" : "Add" }}</button>',
    '  </div>',
    '  <p v-if="refusal" class="warn-line">{{ refusal }}</p>',
    '  <p v-else-if="left !== null" class="setting__hint">',
    '    {{ left }} of {{ rows.length + left }} accounts left on this plan.</p>',
    '  <p v-else class="setting__hint">This plan does not limit how many accounts you have.</p>',
    '</form>',
    '',
    '<p v-if="state.error" class="warn-line">{{ state.error }}</p>',
    '<p v-if="state.loading" class="appt-empty">Loading…</p>',
    '',
    '<ul class="people">',
    '  <li v-for="row in rows" :key="row.userId" class="people__row">',
    '    <span class="people__who">',
    '      <strong>{{ row.email || row.userId }}</strong>',
    '      <span v-if="row.userId === you" class="people__you">you</span>',
    '      <span class="people__seen">{{ since(row.lastSignInAt) }}</span>',
    '    </span>',
    '',
    /* The role control is a pair of tabs rather than a dropdown: there are two
       of them and the current one should be readable without opening anything.
       The one that would strand the salon is disabled with the reason on it,
       which is the same sentence the trigger would have raised. */
    '    <span class="people__roles">',
    '      <button type="button" class="btn btn--tab btn--sm"',
    '        :class="{ \'is-on\': row.role === \'owner\' }"',
    '        :disabled="state.busy" @click="changeRole(row, \'owner\')">Owner</button>',
    '      <button type="button" class="btn btn--tab btn--sm"',
    '        :class="{ \'is-on\': row.role === \'station\' }"',
    '        :disabled="state.busy || strands(row, \'station\')"',
    '        :title="strands(row, \'station\') ? \'This is the salon\\u2019s only owner. Make somebody else an owner first.\' : \'\'"',
    '        @click="changeRole(row, \'station\')">Front desk</button>',
    /* A role set from the admin app (admin/server/schema/15-roles.sql). Shown
       by its id so it is not mistaken for either tab; choosing a tab moves the
       person to that role, which an owner is allowed to do. */
    '      <span v-if="row.role !== \'owner\' && row.role !== \'station\'" class="people__you"',
    '        title="A role set by whoever looks after your subscription.">{{ row.role }}</span>',
    '    </span>',
    '',
    '    <span class="people__act">',
    '      <template v-if="confirmRemove === row.userId">',
    '        <span class="people__ask">Remove?</span>',
    '        <button type="button" class="btn btn--danger btn--sm" :disabled="state.busy"',
    '          @click="doRemove(row)">Yes</button>',
    '        <button type="button" class="btn btn--ghost btn--sm"',
    '          @click="confirmRemove = null">No</button>',
    '      </template>',
    '      <button v-else type="button" class="btn btn--danger-ghost btn--sm"',
    '        :disabled="state.busy || strands(row, null)"',
    '        :title="strands(row, null) ? \'This is the salon\\u2019s only owner. Make somebody else an owner first.\' : \'\'"',
    '        @click="confirmRemove = row.userId">Remove</button>',
    '    </span>',
    '  </li>',
    '</ul>',
    '',
    /* Removing an account does not delete it, and saying so matters: an owner
       who expects "remove" to mean "gone" would not think to ask why the
       address cannot be reused. */
    '<p class="config__where">Removing somebody takes away their access to this salon',
    '  immediately. The account itself is not deleted, so the address cannot be added',
    '  again here &mdash; ask whoever looks after your subscription if you need that.</p>'
  ].join('\n')
};
